export interface Topic {
  id: number;
  name: string;
  description: string | null;
  locked: boolean;
  size: number;
  avg_distance: number;
  p95_distance: number;
  updated_at: string;
  created_at: string;
}

export interface TopicSample {
  id: number;
  content: string;
  distance: number;
  created_at: string;
  user_email: string;
}

export interface TopicsResponse {
  topics: Topic[];
  meta: {
    current_page: number;
    total_pages: number;
    total_count: number;
    per_page: number;
  };
}

export interface TopicSamplesResponse {
  samples: TopicSample[];
  meta?: {
    current_page: number;
    total_pages: number;
    total_count: number;
    per_page: number;
  };
}

export interface TopicsMetricsResponse {
  total_topics: number;
  locked_count: number;
  median_size: number;
  p95_size: number;
}

export interface TopicsParams {
  q?: string;
  locked?: 'true' | 'false' | 'any';
  sort?: 'size' | 'updated_at' | 'avg_distance';
  order?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

export async function getTopics(params: TopicsParams = {}): Promise<TopicsResponse> {
  const searchParams = new URLSearchParams();

  if (params.q) searchParams.append('q', params.q);
  if (params.locked && params.locked !== 'any') searchParams.append('locked', params.locked);
  if (params.sort) searchParams.append('sort', params.sort);
  if (params.order) searchParams.append('order', params.order);
  if (params.page) searchParams.append('page', params.page.toString());
  if (params.per_page) searchParams.append('per_page', params.per_page.toString());

  const response = await fetch(`/ops/topics?${searchParams.toString()}`, {
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
    },
    cache: 'no-store',
    credentials: 'same-origin',
    keepalive: false,
    referrerPolicy: 'same-origin',
    mode: 'same-origin' as any,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch topics: ${response.statusText}`);
  }

  return response.json();
}

export async function getTopicsMetrics(): Promise<TopicsMetricsResponse> {
  const response = await fetch(`/ops/topics/metrics`, {
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
    },
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch topics metrics: ${response.statusText}`);
  }

  return response.json();
}

export async function getTopicSamples(
  topicId: number,
  options: { page?: number; per_page?: number; limit?: number } | number = 5
): Promise<TopicSamplesResponse> {
  const params = new URLSearchParams();
  if (typeof options === 'number') {
    params.set('limit', String(options));
  } else {
    if (options.page) params.set('page', String(options.page));
    if (options.per_page) params.set('per_page', String(options.per_page));
    if (options.limit) params.set('limit', String(options.limit));
  }

  const response = await fetch(`/ops/topics/${topicId}/samples?${params.toString()}`, {
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
    },
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch topic samples: ${response.statusText}`);
  }

  return response.json();
}

export async function updateTopic(topicId: number, updates: { name?: string; locked?: boolean }): Promise<{ topic: Topic }> {
  const response = await fetch(`/ops/topics/${topicId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
    },
    body: JSON.stringify(updates),
    cache: 'no-store',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.errors?.join(', ') || `Failed to update topic: ${response.statusText}`);
  }

  return response.json();
}

export async function mergeTopic(sourceTopicId: number, targetTopicId: number): Promise<{ enqueued: boolean; source_id: number; target_id: number }> {
  const response = await fetch(`/ops/topics/${sourceTopicId}/merge`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
    },
    body: JSON.stringify({ target_id: targetTopicId }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.errors?.join(', ') || `Failed to merge topic: ${response.statusText}`);
  }

  return response.json();
}

export async function recomputeTopic(topicId: number): Promise<{ topic: Topic }> {
  const response = await fetch(`/ops/topics/${topicId}/recompute`, {
    method: 'POST',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.errors?.join(', ') || `Failed to recompute topic: ${response.statusText}`);
  }

  return response.json();
}

export async function dissolveTopic(sourceTopicId: number): Promise<{ enqueued: boolean; topic_id: number }> {
  const response = await fetch(`/ops/topics/${sourceTopicId}/dissolve`, {
    method: 'POST',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.errors?.join(', ') || `Failed to dissolve topic: ${response.statusText}`);
  }

  return response.json();
}
